import type { DegradationCapability } from "@m8t-stack/install-contract";

/**
 * Collector for advisory messages that must survive `--output json`.
 *
 * Commands render progress through a `log` closure that is a NO-OP in json mode.
 * That is right for progress and wrong for advisories: it means the automated
 * install path - the one with nobody watching - is the only path that sees
 * nothing. Anything a human would want to act on goes here instead, so it lands
 * on the JSON result and reaches the installer's status doc.
 *
 * ONE unlevelled channel, deliberately. A severity axis with a single member is
 * noise. Where a producer genuinely needs structure it gets its own sink rather
 * than a level on this one — see `DegradationSink` below, which exists because a
 * consumer has to BRANCH on which capability is missing, and no amount of severity
 * on a sentence lets it do that.
 */
export interface WarningSink {
  add(msg: string): void;
  list(): string[];
}

export function makeWarningSink(echo?: (msg: string) => void): WarningSink {
  const items: string[] = [];
  return {
    add(msg: string): void { items.push(msg); echo?.(msg); },
    list(): string[] { return items.slice(); },
  };
}

/**
 * A capability the command did NOT provision, on a run that otherwise succeeded.
 *
 * SEPARATE FROM WarningSink because the consumer is different in kind. A warning
 * is read by a person; a degradation is read by a surface that must decide what to
 * tell the user, and deciding needs a slug it can switch on. The 2026-08-31 install
 * warned in prose that outbound email was not provisioned, reported success, and
 * nothing downstream could tell the two apart.
 *
 * `capability` is a slug from the CLOSED set in @m8t-stack/install-contract
 * (status-doc.ts, DEGRADATION_CAPABILITIES). `reason` and `remedy` are prose: safe
 * to show, never to parse.
 */
export interface Degradation {
  capability: DegradationCapability;
  reason: string;
  remedy: string;
}

export interface DegradationSink {
  add(d: Degradation): void;
  list(): Degradation[];
}

export function makeDegradationSink(echo?: (d: Degradation) => void): DegradationSink {
  const items: Degradation[] = [];
  return {
    add(d: Degradation): void { items.push(d); echo?.(d); },
    list(): Degradation[] { return items.slice(); },
  };
}
